import React, {
  FunctionComponent,
  ReactNode,
  useRef,
  useState,
  useEffect,
  AriaAttributes,
} from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../helpers";
import uniqueid from "lodash.uniqueid";

interface Props extends AriaAttributes {
  children: ReactNode;
  hasValue?: boolean;
  hideClear?: boolean;
  label: string;
  name?: string;
  onApply: () => void;
  onClear?: () => void;
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  size?: "wide" | "widest";
}

const CCXPBaseDrop: FunctionComponent<Props> = ({
  "aria-labelledby": ariaLabelledBy,
  children,
  hasValue = false,
  hideClear = false,
  label,
  name,
  onApply,
  onClear,
  onMenuClose,
  onMenuOpen,
  size = "",
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const t = useTranslate();

  const clickHandler = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;

    if (!menuRef?.current?.contains(target)) {
      if (onMenuClose) {
        onMenuClose();
      }
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (onMenuOpen) {
        onMenuOpen();
      }
      document.addEventListener("click", clickHandler);
    } else {
      document.removeEventListener("click", clickHandler);
    }
  }, [open]);

  const controlLabel = label.split(" ").join("-");

  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonId = uniqueid("inner-CCXPBase-button-");

  return (
    <div className="CCXPBase" ref={menuRef} data-name={name}>
      <button
        className={`CCXPBase__button ${
          hasValue && !open ? "CCXPBase__button--withValue" : ""
        }`}
        onClick={(): void => setOpen(!open)}
        id={buttonId}
        aria-expanded={open}
        aria-labelledby={`${ariaLabelledBy} ${buttonId}`}
        aria-controls={`control-${controlLabel}`}
        ref={buttonRef}
      >
        {label}{" "}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          className={`ds-u-margin-left--1 ${open ? "CCXPBase__rotate" : ""}`}
        >
          <path
            fill="#323A45"
            fillRule="nonzero"
            d="M11.681 15.87l-5.549-5.39a.435.435 0 0 1 0-.624l.743-.727a.457.457 0 0 1 .637 0L12 13.48l4.488-4.35a.457.457 0 0 1 .637 0l.743.726a.435.435 0 0 1 0 .625l-5.55 5.39a.457.457 0 0 1-.637 0z"
          />
        </svg>
      </button>
      <div
        className={`CCXPBase__menu ${size ? `CCXPBase__menu--${size}` : ""} ${
          open ? "CCXPBase__menu--open" : ""
        }`}
        id={`control-${controlLabel}`}
      >
        <div className="CCXPBase__menuButtonContainer">
          <button
            className="CCXPBase__closeMenuButton"
            onClick={(): void => {
              setOpen(false);
              buttonRef.current?.focus();
            }}
            aria-label={t("fancy_dropdown.close_filter")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              preserveAspectRatio="xMidYMid meet"
              focusable="false"
            >
              <path
                fillRule="nonzero"
                d="M13.6 12l5.258-5.257a.485.485 0 0 0 0-.687l-.914-.914a.485.485 0 0 0-.687 0L12 10.4 6.743 5.142a.485.485 0 0 0-.687 0l-.914.914a.485.485 0 0 0 0 .687L10.4 12l-5.258 5.257a.485.485 0 0 0 0 .687l.914.914c.19.19.497.19.687 0L12 13.6l5.257 5.258c.19.19.497.19.687 0l.914-.914a.485.485 0 0 0 0-.687L13.6 12z"
              ></path>
            </svg>
          </button>
        </div>

        {children}

        <div className="CCXPBase__menuButtonContainer">
          {!hideClear && (
            <Button
              className="ds-c-button--transparent ds-u-margin-top--2"
              onClick={(): void => {
                if (onClear) {
                  onClear();
                }
              }}
            >
              {t("filter_clear")}
            </Button>
          )}
          <Button
            className="ds-c-button--primary-alt ds-u-margin-top--2"
            onClick={(): void => {
              setOpen(false);
              buttonRef.current?.focus();
              onApply();
            }}
            data-testid="apply-btn"
          >
            {t("pharm_selection.apply")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CCXPBaseDrop;
